<script setup lang="ts">
import type { CoverArtPresignFn } from '~/composables/use-cover-art';
import { useIdeaFormStore } from '~/stores/use-idea-form-store';
import {
  IDEA_DESCRIPTION_MAX,
  IDEA_TITLE_MAX,
  ideaLocationRules,
  ideaTitleRules,
} from '~/utils/idea-validation';

// Shared authoring form for Event Ideas — used by both `/ideas/create` and
// `/ideas/[id]/edit`. Purely a form: the owning page holds the API calls,
// the 422 → `serverErrors` mapping, analytics, and navigation.

const props = withDefaults(
  defineProps<{
    /**
     * Cover-art transport. The page owns it because the idea must ALREADY
     * exist before it can be presigned against — on create that id only comes
     * into being partway through submit.
     */
    presign: CoverArtPresignFn;
    submitLabel?: string;
    disabled?: boolean;
    /**
     * Page-level submit failure, rendered NEXT TO the submit button rather than
     * at the top of the page — the author's attention is already down here, and
     * a top-of-page alert reads as a separate, contradictory outcome when the
     * cover uploader has surfaced something of its own.
     */
    submitError?: string | null;
  }>(),
  { submitLabel: 'Float this idea', disabled: false, submitError: null },
);

const emit = defineEmits<{ submit: []; 'clear-error': [] }>();

const store = useIdeaFormStore();

const form = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const coverImageUpload = ref<{
  uploadCoverArt: () => Promise<{ success: boolean; error?: string }>;
} | null>(null);

const descriptionError = ref<string | null>(null);

// The location field's coordinates come ONLY from a genuine Places pick.
// `AddressAutocomplete` emits `update:modelValue` for free text and, on a real
// selection, emits `place-selected` and THEN re-emits `update:modelValue` with
// the resolved display name. Remembering that resolved string is what stops the
// trailing re-emit from wiping the coordinates it just delivered.
let resolvedDisplay: string | null = null;

function onLocationInput(value: string) {
  store.locationDisplay = value ?? '';
  if (store.locationDisplay !== resolvedDisplay) {
    store.latitude = null;
    store.longitude = null;
  }
}

function onPlaceSelected(data: {
  address: string;
  lat: number;
  lng: number;
  name?: string;
}) {
  resolvedDisplay = data.name ?? data.address;
  store.locationDisplay = resolvedDisplay;
  store.latitude = data.lat;
  store.longitude = data.lng;
}

// Free text alone is not a location [S1] — the feed's bounding-box filter reads
// the denormalized lat/lng, so an uncoordinated idea is invisible on every
// map-driven surface.
const locationRules = computed(() => [
  ...ideaLocationRules,
  () =>
    (store.latitude !== null && store.longitude !== null) ||
    'Pick a location from the suggestions so we can place it on the map',
]);

function validateDescription(): boolean {
  const body = store.description.trim();
  if (!body) {
    descriptionError.value = 'A description is required';
    return false;
  }
  if (body.length > IDEA_DESCRIPTION_MAX) {
    descriptionError.value = `Description must be ${IDEA_DESCRIPTION_MAX} characters or fewer`;
    return false;
  }
  descriptionError.value = null;
  return true;
}

async function validate(): Promise<{ valid: boolean }> {
  const result = await form.value?.validate();
  // The markdown editor is not a Vuetify input, so v-form never sees it.
  const descriptionOk = validateDescription();
  return { valid: (result?.valid ?? false) && descriptionOk };
}

// The submit button is locked for the whole submit sequence, not just the API
// call — on create that sequence is POST-then-upload, and a second click would
// float a second idea.
const submitDisabled = computed(() => props.disabled || store.isSubmitting);

async function handleSubmit() {
  if (submitDisabled.value) return;
  const { valid } = await validate();
  if (valid) emit('submit');
}

defineExpose({
  validate,
  uploadCoverArt: () =>
    coverImageUpload.value?.uploadCoverArt() ??
    Promise.resolve({ success: true }),
});
</script>

<template>
  <v-form ref="form" @submit.prevent="handleSubmit">
    <v-text-field
      v-model="store.title"
      label="What's the idea?"
      hint="A rough working title — you can sharpen it later"
      :rules="ideaTitleRules"
      :error-messages="store.serverErrors.title"
      :disabled="props.disabled"
      :counter="IDEA_TITLE_MAX"
      class="mb-2"
    />

    <label class="text-body-2 font-weight-medium d-block mb-1">
      Describe it (Markdown supported)
    </label>
    <ClientOnly>
      <MdEditorV3
        v-model="store.description"
        language="en-US"
        :preview="false"
        :read-only="props.disabled"
        :toolbars-exclude="[
          'save',
          'htmlPreview',
          'catalog',
          'github',
          'image',
        ]"
        style="height: 260px"
      />
      <template #fallback>
        <v-textarea
          v-model="store.description"
          label="Describe it"
          rows="8"
          :disabled="props.disabled"
        />
      </template>
    </ClientOnly>
    <div v-if="descriptionError" class="text-error text-caption mt-1">
      {{ descriptionError }}
    </div>
    <div
      v-else-if="store.serverErrors.description?.length"
      class="text-error text-caption mt-1"
    >
      {{ store.serverErrors.description[0] }}
    </div>
    <div class="text-caption text-medium-emphasis mt-1 mb-4">
      {{ store.description.length }} / {{ IDEA_DESCRIPTION_MAX }} characters
    </div>

    <IdeasFormIdeaTimingControl :disabled="props.disabled" class="mb-4" />

    <div class="text-subtitle-2 font-weight-medium mb-1 mt-2">
      Roughly where?
    </div>
    <ClientOnly>
      <EventsCreateAddressAutocomplete
        :model-value="store.locationDisplay"
        label="Location"
        hint="Pick a suggestion — we place ideas on the map by their coordinates"
        :rules="locationRules"
        :error-messages="
          store.serverErrors['location_hint.display'] ??
          store.serverErrors['location_hint.latitude']
        "
        :disabled="props.disabled"
        @update:model-value="onLocationInput"
        @place-selected="onPlaceSelected"
      />
      <template #fallback>
        <v-text-field
          :model-value="store.locationDisplay"
          label="Location"
          :disabled="props.disabled"
          class="mb-2"
          @update:model-value="onLocationInput"
        />
      </template>
    </ClientOnly>

    <div class="mb-4">
      <label class="text-body-2 font-weight-medium d-block mb-1">
        Cover image (optional)
      </label>
      <!-- The idea resource exposes only a flat `cover_image_url` — there is no
           focal-point/crops envelope to rehydrate, so an existing cover can be
           shown and replaced but not re-cropped in place. -->
      <div v-if="store.existingCoverImageUrl" class="mb-3">
        <v-img
          :src="store.existingCoverImageUrl"
          alt="Current cover image"
          :aspect-ratio="16 / 9"
          cover
          rounded="lg"
          class="bg-surface-variant"
          style="max-width: 360px"
        />
        <div class="text-caption text-medium-emphasis mt-1">
          This is your current cover. Replacing the image lets you re-crop it.
        </div>
      </div>
      <!-- The uploader's own upload-failure alert is suppressed here: its
           message is returned to the page, which re-renders it as
           `submitError` below the form. Two alerts saying different things
           about the same failure is what this opt-out exists to prevent. -->
      <CommonCoverImageUpload
        ref="coverImageUpload"
        :controller="store"
        :presign="props.presign"
        :disabled="props.disabled"
        subject-noun="idea"
        suppress-upload-error-alert
      />
    </div>

    <v-alert
      v-if="props.submitError"
      type="error"
      variant="tonal"
      density="compact"
      class="mt-4"
      closable
      data-testid="idea-submit-error"
      @click:close="emit('clear-error')"
    >
      {{ props.submitError }}
    </v-alert>

    <div class="d-flex justify-end mt-4">
      <v-btn
        color="primary"
        type="submit"
        :loading="store.isSubmitting"
        :disabled="submitDisabled"
      >
        {{ props.submitLabel }}
      </v-btn>
    </div>
  </v-form>
</template>
